// API service for backend communication

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.gwrepartners.com/api';

// Cache for counties data to avoid repeated API calls
let countiesCache = null;

// Helper function to handle API responses
const handleResponse = async (response) => {
  if (!response.ok) {
    const errorText = await response.text();
    
    // Check for 401 Unauthorized error
    if (response.status === 401) {
      // Clear token and trigger logout
      localStorage.removeItem('token');
      // Dispatch a custom event to notify the app about the auth error
      window.dispatchEvent(new CustomEvent('authError', { detail: 'Session expired. Please log in again.' }));
      throw new Error('Session expired. Please log in again.');
    }
    
    // Try to parse the error as JSON to extract the detail message
    try {
      const errorJson = JSON.parse(errorText);
      
      // Create an error object that preserves the JSON structure
      const error = new Error(errorJson.detail || errorJson.message || JSON.stringify(errorJson));
      error.response = errorJson; // Attach the full response for error handling
      error.status = response.status;
      
      throw error;
    } catch (parseError) {
      // If parsing fails, use the original error text
      throw new Error(errorText);
    }
  }
  return response.json();
};

// Helper function to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

// API functions
export const api = {
  // Get counties from backend
  getCounties: async () => {
    try {
      // Return cached data if available
      if (countiesCache) {
        return countiesCache;
      }

      const response = await fetch(`${API_BASE_URL}/v1/counties/`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
      });
      
      const data = await handleResponse(response);
      countiesCache = data.counties || [];
      return countiesCache;
    } catch (error) {
      console.error('Error fetching counties:', error);
      throw error;
    }
  },

  // Get states (extracted from counties data)
  getStates: async () => {
    try {
      const counties = await api.getCounties();
      const states = [...new Set(counties.map(county => county.state_name))];
      return states.sort();
    } catch (error) {
      console.error('Error fetching states:', error);
      throw error;
    }
  },

  // Get counties for a specific state
  getCountiesByState: async (stateName) => {
    try {
      const counties = await api.getCounties();
      const filteredCounties = counties.filter(county => county.state_name === stateName);
      // Return full county objects instead of just names to include has_flu_data and has_zoning_data
      return filteredCounties;
    } catch (error) {
      console.error('Error fetching counties by state:', error);
      throw error;
    }
  },

  // Get state code for a specific state name
  getStateCode: async (stateName) => {
    try {
      const counties = await api.getCounties();
      const stateCounty = counties.find(county => county.state_name === stateName);
      return stateCounty ? stateCounty.state_code.toLowerCase() : stateName.toLowerCase();
    } catch (error) {
      console.error('Error getting state code:', error);
      return stateName.toLowerCase();
    }
  },

  // Helper function to format county name for API
  formatCountyForAPI: (countyName) => {
    return countyName.toLowerCase();
  },

  // Get future land use and zoning data for a specific jurisdiction
  getJurisdictionData: async (state, county) => {
    try {
      const stateCode = await api.getStateCode(state);
      const countyName = api.formatCountyForAPI(county);
      const url = `${API_BASE_URL}/v1/parcels/jurisdictions/${encodeURIComponent(stateCode)}/${encodeURIComponent(countyName)}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
      });
      
      const data = await handleResponse(response);
      return data;
    } catch (error) {
      console.error('Error fetching jurisdiction data:', error);
      throw error;
    }
  },

  // Get future land use options for a specific jurisdiction
  getFutureLandUse: async (state, county) => {
    try {
      const jurisdictionData = await api.getJurisdictionData(state, county);
      if (!jurisdictionData.future_land_use_codes) {
        console.warn('No future land use codes found for jurisdiction');
        return [];
      }
      const futureLandUseCodes = jurisdictionData.future_land_use_codes
        .filter(item => item.category === 'Future Land Use')
        .map(item => item.land_use_code);
      return futureLandUseCodes;
    } catch (error) {
      console.error('Error fetching future land use:', error);
      return [];
    }
  },

  // Get in-place zoning options for a specific jurisdiction
  getInPlaceZoning: async (state, county) => {
    try {
      const jurisdictionData = await api.getJurisdictionData(state, county);
      if (!jurisdictionData.zoning_codes) {
        console.warn('No zoning codes found for jurisdiction');
        return [];
      }
      const zoningCodes = jurisdictionData.zoning_codes
        .filter(item => item.category === 'In-Place Zoning')
        .map(item => item.land_use_code);
      return zoningCodes;
    } catch (error) {
      console.error('Error fetching in-place zoning:', error);
      return [];
    }
  },

  // Search parcels based on filters (async - background task)
  filterParcelsAsync: async (filters) => {
    try {
      // Determine filter type based on the filters
      let filterType;
      if (filters.filterType === 'single') {
        filterType = filters.filterMode === 'futureLandUse' 
          ? 'single_parcel_future_land_use' 
          : 'single_parcel_in_place_zoning';
      } else {
        filterType = filters.filterMode === 'futureLandUse' 
          ? 'assembly_future_land_use' 
          : 'assembly_in_place_zoning';
      }

      // Get state code
      const stateCode = await api.getStateCode(filters.state);
      
      // Format county name
      const countyName = api.formatCountyForAPI(filters.county);

      // Prepare land use codes array
      const landUseCodes = [];
      if (filters.filterMode === 'futureLandUse') {
        if (filters.futureLandUseMulti && filters.futureLandUseMulti.length > 0) {
          // For both single and multi parcel filters, use multiple FLU selections
          landUseCodes.push(...filters.futureLandUseMulti);
        }
      } else if (filters.filterMode === 'inPlaceZoning' && filters.inPlaceZoning) {
        landUseCodes.push(filters.inPlaceZoning);
      }

      // Build request payload
      const payload = {
        filter_type: filterType,
        state: stateCode,
        county: countyName,
        land_use_codes: landUseCodes,
        min_acres: filters.minAcreage ? parseFloat(filters.minAcreage) : 0,
        max_acres: filters.maxAcreage ? parseFloat(filters.maxAcreage) : 0,
        max_assessment_per_acre: filters.maxAssessedValuePerAcre ? parseFloat(filters.maxAssessedValuePerAcre) : 0,
        bbox: filters.bbox || null, // Use bbox from filters if provided
        inSR: 4326,
        limit: 10000,
        include_geometry: false,
        include_center: true
      };

      const response = await fetch(`${API_BASE_URL}/v1/parcels/filter-async`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify(payload)
      });

      const data = await handleResponse(response);
      return data;
    } catch (error) {
      console.error('Error starting async parcel filter:', error);
      throw error;
    }
  },

  // Check status of async parcel filter task
  checkParcelFilterStatus: async (taskId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/parcels/filter-async/${taskId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
      });

      const data = await handleResponse(response);
      return data;
    } catch (error) {
      console.error('Error checking parcel filter status:', error);
      throw error;
    }
  },

  // Search parcels based on filters (legacy - kept for backward compatibility)
  searchParcels: async (filters) => {
    try {
      // Determine filter type based on the filters
      let filterType;
      if (filters.filterType === 'single') {
        filterType = filters.filterMode === 'futureLandUse' 
          ? 'single_parcel_future_land_use' 
          : 'single_parcel_in_place_zoning';
      } else {
        filterType = filters.filterMode === 'futureLandUse' 
          ? 'assembly_future_land_use' 
          : 'assembly_in_place_zoning';
      }

      // Get state code
      const stateCode = await api.getStateCode(filters.state);
      
      // Format county name
      const countyName = api.formatCountyForAPI(filters.county);

      // Prepare land use codes array
      const landUseCodes = [];
      if (filters.filterMode === 'futureLandUse') {
        if (filters.futureLandUseMulti && filters.futureLandUseMulti.length > 0) {
          // For both single and multi parcel filters, use multiple FLU selections
          landUseCodes.push(...filters.futureLandUseMulti);
        }
      } else if (filters.filterMode === 'inPlaceZoning' && filters.inPlaceZoning) {
        landUseCodes.push(filters.inPlaceZoning);
      }

      // Build request payload
      const payload = {
        filter_type: filterType,
        state: stateCode,
        county: countyName,
        land_use_codes: landUseCodes,
        min_acres: filters.minAcreage ? parseFloat(filters.minAcreage) : 0,
        max_acres: filters.maxAcreage ? parseFloat(filters.maxAcreage) : 0,
        max_assessment_per_acre: filters.maxAssessedValuePerAcre ? parseFloat(filters.maxAssessedValuePerAcre) : 0,
        bbox: filters.bbox || null, // Use bbox from filters if provided
        inSR: 4326,
        limit: 10000,
        include_geometry: false,
        include_center: true
      };

      const response = await fetch(`${API_BASE_URL}/v1/parcels/filter`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify(payload)
      });

      const data = await handleResponse(response);

      return {
        parcels: data.parcels || [],
        assemblages: data.assemblages || [],
        totalCount: data.total_count || 0,
        filterSummary: data.filter_summary || {}
      };
    } catch (error) {
      console.error('Error searching parcels:', error);
      throw error;
    }
  },

  // Generate parcel border GeoJSON
  generateParcelBorder: async (parcelIds, state, county) => {
    try {
      const stateCode = await api.getStateCode(state);
      const countyName = api.formatCountyForAPI(county);
      
      const url = `${API_BASE_URL}/v1/parcels/generate-raster?state=${stateCode}&county=${encodeURIComponent(countyName)}&format=geojson&output_filename=tile`;
      
      const parcelIdsArray = JSON.parse(parcelIds);

      // Ensure parcelIds is an array and send it properly
      const requestBody = Array.isArray(parcelIdsArray) ? parcelIdsArray : [String(parcelIdsArray)];
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify(requestBody)
      });

      const data = await handleResponse(response);

      if (data.success && data.geojson) {
        // Parse the GeoJSON string to get the actual geometry
        const geojsonData = data.geojson;
        return {
          success: true,
          geojson: geojsonData,
          parcelCount: data.parcel_count,
          totalArea: data.total_area,
          downloadUrl: data.download_url
        };
      } else {
        throw new Error(data.message || 'Failed to generate parcel border');
      }
    } catch (error) {
      console.error('Error generating parcel border:', error);
      throw error;
    }
  },

  // Get parcel information by coordinates
  getParcelByCoordinates: async (latitude, longitude, state = 'ca', county = 'orange') => {
    try {
      // Format state and county names properly
      const stateCode = await api.getStateCode(state);
      const countyName = api.formatCountyForAPI(county);
      
      const url = `${API_BASE_URL}/v1/parcels/${encodeURIComponent(stateCode)}/${encodeURIComponent(countyName)}/coordinates`;
      
      const payload = {
        include_center: false,
        include_geometry: true,
        latitude: latitude,
        longitude: longitude
      };

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify(payload)
      });

      const data = await handleResponse(response);

      return {
        success: true,
        parcel: data || null,
        found: data || false
      };
    } catch (error) {
      console.error('Error getting parcel by coordinates:', error);
      return {
        success: false,
        parcel: null,
        found: false,
        error: error.message
      };
    }
  },

  // Get detailed parcel information by parcel ID
  getParcelDetails: async (parcelId, state = 'ca', county = 'orange') => {
    try {
      // Format state and county names properly
      const stateCode = await api.getStateCode(state);
      const countyName = api.formatCountyForAPI(county);
      
      const url = `${API_BASE_URL}/v1/parcels/${encodeURIComponent(stateCode)}/${encodeURIComponent(countyName)}/${parcelId}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
      });

      const data = await handleResponse(response);

      return {
        success: true,
        parcel: data || null,
        found: data || false
      };
    } catch (error) {
      console.error('Error getting parcel details:', error);
      return {
        success: false,
        parcel: null,
        found: false,
        error: error.message
      };
    }
  },

  // Dashboard API functions
  getDashboardData: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/dashboard/stats`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
      });

      const data = await handleResponse(response);
      return data;
    } catch (error) {
      console.error('Error getting dashboard data:', error);
      throw error;
    }
  },

  getCountyData: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/counties/`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
      });

      const data = await handleResponse(response);
      return data;
    } catch (error) {
      console.error('Error getting county data:', error);
      throw error;
    }
  },

  getCountyDetails: async (stateCode, countyName) => {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/counties/${encodeURIComponent(stateCode)}/${encodeURIComponent(countyName)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
      });

      const data = await handleResponse(response);
      return data;
    } catch (error) {
      console.error('Error getting county details:', error);
      throw error;
    }
  },

  addCounty: async (countyData) => {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/counties/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify({
          state_name: countyData.state,
          state_code: countyData.stateCode,
          county_name: countyData.name
        })
      });

      const data = await handleResponse(response);
      return data;
    } catch (error) {
      console.error('Error adding county:', error);
      throw error;
    }
  },

  updateCounty: async (stateCode, countyName, countyData) => {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/counties/${encodeURIComponent(stateCode)}/${encodeURIComponent(countyName)}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify({
          state_name: countyData.state,
          state_code: countyData.stateCode,
          county_name: countyData.name
        })
      });

      const data = await handleResponse(response);
      return data;
    } catch (error) {
      console.error('Error updating county:', error);
      throw error;
    }
  },

  deleteCounty: async (stateCode, countyName) => {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/counties/${encodeURIComponent(stateCode)}/${encodeURIComponent(countyName)}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
      });

      const data = await handleResponse(response);
      return data;
    } catch (error) {
      console.error('Error deleting county:', error);
      throw error;
    }
  },

  updateParcelData: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/dashboard/update-parcels`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
      });

      const data = await handleResponse(response);
      return data;
    } catch (error) {
      console.error('Error updating parcel data:', error);
      throw error;
    }
  },

  setUpdateCycle: async (updateCycleConfig) => {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/dashboard/update-cycle`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify(updateCycleConfig)
      });

      const data = await handleResponse(response);
      return data;
    } catch (error) {
      console.error('Error setting update cycle:', error);
      throw error;
    }
  },

  // Analyze parcels by county
  analyzeCountyParcels: async (state, county) => {
    try {
      const stateCode = await api.getStateCode(state);
      const countyName = api.formatCountyForAPI(county);
      
      const response = await fetch(`${API_BASE_URL}/v1/dashboard/analyze-county`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify({
          state: stateCode,
          county: countyName
        })
      });

      const data = await handleResponse(response);
      return data;
    } catch (error) {
      console.error('Error analyzing county parcels:', error);
      throw error;
    }
  },

  // Authentication API functions
  login: async (username, password) => {
    try {
      const formData = new URLSearchParams();
      formData.append('username', username);
      formData.append('password', password);

      const response = await fetch(`${API_BASE_URL}/v1/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData
      });

      const data = await handleResponse(response);
      
      // Get user info after successful login
      const userResponse = await fetch(`${API_BASE_URL}/v1/auth/me`, {
        headers: {
          'Authorization': `Bearer ${data.access_token}`,
        },
      });
      
      const user = await handleResponse(userResponse);
      
      return {
        access_token: data.access_token,
        user: user
      };
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  },

  register: async (userData) => {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData)
      });

      const user = await handleResponse(response);
      
      // Auto-login after registration
      const loginResponse = await api.login(userData.username, userData.password);
      
      return {
        access_token: loginResponse.access_token,
        user: user
      };
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    }
  },

  getCurrentUser: async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No token found');
      }

      const response = await fetch(`${API_BASE_URL}/v1/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      const data = await handleResponse(response);
      return data;
    } catch (error) {
      console.error('Get current user error:', error);
      throw error;
    }
  },

  updateCurrentUser: async (userData) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No token found');
      }

      const response = await fetch(`${API_BASE_URL}/v1/auth/me`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData)
      });

      const data = await handleResponse(response);
      return data;
    } catch (error) {
      console.error('Update current user error:', error);
      throw error;
    }
  },

  updateCurrentUserPassword: async (passwordData) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No token found');
      }

      const response = await fetch(`${API_BASE_URL}/v1/auth/me/password`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(passwordData)
      });

      const data = await handleResponse(response);
      return data;
    } catch (error) {
      console.error('Update current user password error:', error);
      throw error;
    }
  },

  // Superuser functions
  getUsers: async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No token found');
      }

      const response = await fetch(`${API_BASE_URL}/v1/auth/users`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      const data = await handleResponse(response);
      return data;
    } catch (error) {
      console.error('Get users error:', error);
      throw error;
    }
  },

  updateUser: async (userId, userData) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No token found');
      }

      const response = await fetch(`${API_BASE_URL}/v1/auth/users/${userId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData)
      });

      const data = await handleResponse(response);
      return data;
    } catch (error) {
      console.error('Update user error:', error);
      throw error;
    }
  },

  deleteUser: async (userId) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No token found');
      }

      const response = await fetch(`${API_BASE_URL}/v1/auth/users/${userId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      const data = await handleResponse(response);
      return data;
    } catch (error) {
      console.error('Delete user error:', error);
      throw error;
    }
  },

  // Favorites API functions
  createFavorite: async (favoriteData) => {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/favorites/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify(favoriteData)
      });

      const data = await handleResponse(response);
      return data;
    } catch (error) {
      console.error('Create favorite error:', error);
      throw error;
    }
  },

  getFavorites: async (skip = 0, limit = 100) => {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/favorites/?skip=${skip}&limit=${limit}`, {
        method: 'GET',
        headers: {
          ...getAuthHeaders(),
        },
      });

      const data = await handleResponse(response);
      return data;
    } catch (error) {
      console.error('Get favorites error:', error);
      throw error;
    }
  },

  getFavorite: async (favoriteId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/favorites/${favoriteId}`, {
        method: 'GET',
        headers: {
          ...getAuthHeaders(),
        },
      });

      const data = await handleResponse(response);
      return data;
    } catch (error) {
      console.error('Get favorite error:', error);
      throw error;
    }
  },

  updateFavorite: async (favoriteId, favoriteData) => {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/favorites/${favoriteId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify(favoriteData)
      });

      const data = await handleResponse(response);
      return data;
    } catch (error) {
      console.error('Update favorite error:', error);
      throw error;
    }
  },

  deleteFavorite: async (favoriteId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/favorites/${favoriteId}`, {
        method: 'DELETE',
        headers: {
          ...getAuthHeaders(),
        },
      });

      const data = await handleResponse(response);
      return data;
    } catch (error) {
      console.error('Delete favorite error:', error);
      throw error;
    }
  },

  checkFavoriteExists: async (countyId, parcelIds) => {
    try {
      const parcelIdsParam = Array.isArray(parcelIds) ? parcelIds.join(',') : parcelIds;
      const response = await fetch(`${API_BASE_URL}/v1/favorites/check/${countyId}?parcel_ids=${parcelIdsParam}`, {
        method: 'GET',
        headers: {
          ...getAuthHeaders(),
        },
      });

      const data = await handleResponse(response);
      return data;
    } catch (error) {
      console.error('Check favorite exists error:', error);
      throw error;
    }
  },

  getFavoritesNearby: async (latitude, longitude, radiusMiles = 10.0) => {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/favorites/nearby/${latitude}/${longitude}?radius_miles=${radiusMiles}`, {
        method: 'GET',
        headers: {
          ...getAuthHeaders(),
        },
      });

      const data = await handleResponse(response);
      return data;
    } catch (error) {
      console.error('Get favorites nearby error:', error);
      throw error;
    }
  },

  // County Registration and Update API functions
  checkCountyDataAvailability: async (stateCode, stateName, countyName) => {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/counties/check-availability`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify({
          state_code: stateCode,
          state_name: stateName,
          county_name: countyName
        })
      });

      const data = await handleResponse(response);
      return data;
    } catch (error) {
      console.error('Error checking county data availability:', error);
      throw error;
    }
  },

  registerCountyWithData: async (stateCode, stateName, countyName, fluLandUseColumn = null, fluDescriptionColumn = null) => {
    try {
      const payload = {
        state_code: stateCode,
        state_name: stateName,
        county_name: countyName
      };

      // Add optional FLU column parameters if provided
      if (fluLandUseColumn) {
        payload.flu_land_use_column = fluLandUseColumn;
      }
      if (fluDescriptionColumn) {
        payload.flu_description_column = fluDescriptionColumn;
      }

      const response = await fetch(`${API_BASE_URL}/v1/counties/register-with-data`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify(payload)
      });

      const data = await handleResponse(response);
      return data;
    } catch (error) {
      console.error('Error registering county with data:', error);
      throw error;
    }
  },

  updateCountyData: async (stateCode, stateName, countyName, updateType, fluLandUseColumn = null, fluDescriptionColumn = null) => {
    try {
      const payload = {
        state_code: stateCode,
        state_name: stateName,
        county_name: countyName,
        update_type: updateType
      };

      // Add optional FLU column parameters if provided
      if (fluLandUseColumn) {
        payload.flu_land_use_column = fluLandUseColumn;
      }
      if (fluDescriptionColumn) {
        payload.flu_description_column = fluDescriptionColumn;
      }

      const response = await fetch(`${API_BASE_URL}/v1/counties/update-data`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify(payload)
      });

      const data = await handleResponse(response);
      return data;
    } catch (error) {
      console.error('Error updating county data:', error);
      throw error;
    }
  },

  // Task status checking
  checkTaskStatus: async (taskId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/task/${taskId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Check task status error:', error);
      throw error;
    }
  },

  // Vector Tiles API functions
  getTileMetadata: async (state, county) => {
    try {
      const stateCode = await api.getStateCode(state);
      const countyName = api.formatCountyForAPI(county);
      
      const response = await fetch(`${API_BASE_URL}/v1/tiles/${encodeURIComponent(stateCode)}/${encodeURIComponent(countyName)}/metadata`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
      });

      const data = await handleResponse(response);
      return data;
    } catch (error) {
      console.error('Error getting tile metadata:', error);
      throw error;
    }
  },

  getTileCategories: async (state, county) => {
    try {
      const stateCode = await api.getStateCode(state);
      const countyName = api.formatCountyForAPI(county);
      
      const response = await fetch(`${API_BASE_URL}/v1/tiles/${encodeURIComponent(stateCode)}/${encodeURIComponent(countyName)}/categories`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
      });

      const data = await handleResponse(response);
      return data;
    } catch (error) {
      console.error('Error getting tile categories:', error);
      throw error;
    }
  },

  // Generate vector tile URL for use with mapbox-gl
  getVectorTileUrl: async (state, county, layer = 'parcels') => {
    const stateCode = await api.getStateCode(state);
    const countyName = api.formatCountyForAPI(county);
    
    return `${API_BASE_URL}/v1/tiles/${encodeURIComponent(stateCode)}/${encodeURIComponent(countyName)}/{z}/{x}/{y}.pbf?layer=${layer}`;
  }
}; 